<script setup lang="ts">
import { ref } from "vue";

import type { FileSource } from "@/types/album";

/**
 * Port de `.quick-share` (design-reference/styles.css), avec une troisième
 * action « Prendre une photo » ajoutée entre le bouton principal et le
 * message vocal.
 *
 * L'action principale est aussi une zone de dépôt : sur ordinateur, faire
 * glisser ses photos depuis l'explorateur est le geste attendu, et rien ne
 * l'indiquait. Le clic reste le chemin nominal (et le seul sur mobile).
 */

const emit = defineEmits<{
  "open-voice": [];
  /**
   * Fichiers entrants, avec leur provenance : la feuille d'ajout s'en sert pour
   * proposer le bon geste de complément — reprendre une photo plutôt que
   * rouvrir la photothèque quand l'invité vient de l'appareil photo. Le parent
   * n'ouvre la feuille qu'à partir de là — jamais avant.
   */
  files: [files: File[], source: FileSource];
}>();

/**
 * Les deux actions ouvrent leur sélecteur natif immédiatement, et la feuille
 * d'ajout ne s'ouvre qu'une fois les fichiers choisis. Ouvrir la feuille
 * d'abord laissait une modale vide en fond derrière le sélecteur du système,
 * et refermer celui-ci abandonnait l'invité devant un écran sans contenu.
 */
function openInput(input: HTMLInputElement | null): void {
  if (!input) return;
  // Sans cette remise à zéro, re-choisir exactement les mêmes fichiers ne
  // redéclenche pas `change`.
  input.value = "";
  input.click();
}

function readInput(input: HTMLInputElement | null, source: FileSource): void {
  if (!input) return;
  const files = [...(input.files ?? [])];
  input.value = "";
  if (files.length) emit("files", files, source);
}

const libraryInput = ref<HTMLInputElement | null>(null);

/**
 * La prise de vue est déléguée à l'appareil photo du téléphone plutôt qu'à un
 * flux `getUserMedia` affiché dans la page. `getUserMedia` sort le capteur sans
 * traitement — pas de HDR, pas de mode nuit, pas de stabilisation — et à la
 * résolution du flux, soit environ un mégapixel là où le capteur en offre
 * plusieurs dizaines. Pour un album de mariage, souvent photographié en lumière
 * faible, l'écart est trop grand.
 *
 * L'`accept` ne couvre qu'`image/*`, et c'est déterminant : dès qu'il liste
 * plusieurs familles de types, iOS ignore `capture` et retombe sur sa feuille
 * de choix, donc sur la photothèque — soit exactement ce que fait déjà le
 * bouton d'ajout, à côté.
 *
 * `capture` est en revanche ignoré sur ordinateur : le bouton y ouvre le
 * sélecteur de fichiers habituel, faute de caméra à ouvrir.
 */
const cameraInput = ref<HTMLInputElement | null>(null);

const isDragging = ref(false);
/**
 * `dragenter`/`dragleave` se déclenchent aussi au passage sur chaque enfant :
 * un simple booléen clignoterait. On compte les entrées et sorties.
 */
let dragDepth = 0;

function hasFiles(event: DragEvent): boolean {
  return [...(event.dataTransfer?.types ?? [])].includes("Files");
}

function onDragEnter(event: DragEvent): void {
  if (!hasFiles(event)) return;
  dragDepth += 1;
  isDragging.value = true;
}

function onDragOver(event: DragEvent): void {
  if (!hasFiles(event)) return;
  // Sans ce `preventDefault`, le navigateur refuse le dépôt et ouvre le
  // fichier dans l'onglet à la place.
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
}

function onDragLeave(): void {
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) isDragging.value = false;
}

function onDrop(event: DragEvent): void {
  event.preventDefault();
  dragDepth = 0;
  isDragging.value = false;
  const files = [...(event.dataTransfer?.files ?? [])];
  if (files.length) emit("files", files, "library");
}
</script>

<template>
  <section class="quick-share" aria-labelledby="shareTitle">
    <div class="quick-share__copy">
      <h2 id="shareTitle">Vous avez pris de belles photos&nbsp;?</h2>
      <p>Ajoutez-les à l’album de Sarah et Quentin.</p>
    </div>
    <div class="quick-actions">
      <input
        ref="libraryInput"
        type="file"
        accept="image/*,video/*"
        multiple
        class="u-sr-only"
        data-testid="library-input"
        @change="readInput(libraryInput, 'library')"
      />

      <button
        type="button"
        class="primary-action"
        :class="{ 'is-dragging': isDragging }"
        data-testid="open-upload"
        @click="openInput(libraryInput)"
        @dragenter="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <span class="mini-polaroid primary-action__icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 7.5h3l1.5-2h7l1.5 2h3v11H4v-11Z"></path>
            <path d="M12 10v6M9 13h6"></path>
          </svg>
        </span>
        <span>
          <strong>{{ isDragging ? "Déposez vos fichiers ici" : "Ajouter photos & vidéos" }}</strong>
        </span>
      </button>

      <input
        ref="cameraInput"
        type="file"
        accept="image/*"
        capture="environment"
        class="u-sr-only"
        data-testid="camera-input"
        @change="readInput(cameraInput, 'camera')"
      />

      <div class="secondary-actions">
        <button
          type="button"
          class="polaroid-tile"
          data-testid="open-camera"
          @click="openInput(cameraInput)"
        >
          <span class="mini-polaroid">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M4 8.5h3.2l1.3-2h6.9l1.3 2H20a1 1 0 0 1 1 1V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1Z"
              ></path>
              <circle cx="12" cy="13" r="3.3"></circle>
            </svg>
          </span>
          <span class="polaroid-tile__caption">Prendre une photo</span>
        </button>

        <button
          type="button"
          class="polaroid-tile"
          data-testid="open-voice"
          @click="emit('open-voice')"
        >
          <span class="mini-polaroid">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="9" y="3" width="6" height="12" rx="3"></rect>
              <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M9 21h6"></path>
            </svg>
          </span>
          <span class="polaroid-tile__caption">Message vocal</span>
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
/*
 * Mise en scène du premier écran.
 *
 * Le hero ne bouge pas : il porte le LCP, et animer une photo plein cadre
 * retarde sa perception même une fois chargée. C'est l'accroche qui entre,
 * comme un carton qu'on pose sur la table — un seul mouvement, bref, juste
 * après que l'œil a absorbé la photo.
 *
 * Pas de cascade bouton par bouton : le tic est reconnaissable, il retarde le
 * moment où l'interface devient utilisable, et il dilue l'attention entre trois
 * actions qui n'ont pas le même poids. Un seul accent, sur le dépôt de photos.
 */
.quick-share {
  animation: qs-card-in 0.3s cubic-bezier(0.22, 1, 0.36, 1) 80ms both;
}

.quick-share__copy {
  animation: qs-copy-in 0.3s ease-out 280ms both;
}

@keyframes qs-card-in {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
}

@keyframes qs-copy-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}

/*
 * L'action principale s'encre une fois à l'entrée, puis respire lentement.
 * L'oscillation porte sur l'ombre seule : un `scale` en boucle déplacerait la
 * cible sous le pouce d'un invité qui vise d'une main, dans le noir.
 */
.primary-action {
  animation:
    qs-cta-stamp 0.23s ease-out 420ms both,
    qs-cta-breathe 2.6s ease-in-out 900ms infinite;
}

@keyframes qs-cta-stamp {
  from {
    box-shadow:
      0 0 0 6px rgb(255 255 255 / 0),
      0 11px 25px rgb(83 96 76 / 0.17);
  }
  50% {
    box-shadow:
      0 0 0 4px rgb(255 255 255 / 0.3),
      0 11px 25px rgb(83 96 76 / 0.17);
  }
  to {
    box-shadow:
      0 0 0 0 rgb(255 255 255 / 0),
      0 11px 25px rgb(83 96 76 / 0.17);
  }
}

@keyframes qs-cta-breathe {
  0%,
  100% {
    box-shadow: 0 11px 25px rgb(83 96 76 / 0.17);
  }
  50% {
    box-shadow: 0 14px 32px rgb(83 96 76 / 0.32);
  }
}

/* Le survol d'un fichier glissé doit primer sur la respiration. */
.primary-action.is-dragging {
  animation: none;
}

@media (prefers-reduced-motion: reduce) {
  .quick-share,
  .quick-share__copy,
  .primary-action {
    animation: none;
  }
}

.quick-share {
  position: relative;
  z-index: 2;
  margin: -1px 12px 0;
  padding: 30px 18px 22px;
  border-radius: 0 0 24px 24px;
  background:
    linear-gradient(rgb(255 254 249 / 0.93), rgb(255 254 249 / 0.93)),
    url("/assets/watercolor-floral-paper.webp") center 7% / 540px auto;
  box-shadow: 0 20px 55px rgb(47 51 45 / 0.1);
}

.quick-share__copy {
  text-align: center;
}

/* Italiana n'existe qu'en une graisse, très fine : ses déliés disparaissent
   sur un écran mobile en pleine lumière. Le contour épaissit le tracé sans
   changer de police — la largeur est calée sur la taille du texte, sinon les
   grands titres s'empâtent. */
.quick-share h2 {
  margin: 0 0 8px;
  font-family: var(--font-serif);
  font-size: clamp(26px, 6.6vw, 40px);
  font-weight: 400;
  line-height: 1.08;
  -webkit-text-stroke: 0.2px currentColor;
}

.quick-share__copy > p {
  max-width: 490px;
  margin: 0 auto;
  color: var(--color-muted);
  font-size: 14px;
  line-height: 1.6;
}

.quick-actions {
  display: grid;
  gap: 10px;
  margin-top: 22px;
}

/* --- Action principale : la seule ligne pleine largeur, donc la seule qui se
       lit comme l'action centrale. --- */
.primary-action {
  position: relative;
  display: flex;
  align-items: center;
  gap: 13px;
  width: 100%;
  min-height: 66px;
  padding: 8px 14px 8px 8px;
  color: white;
  border: 1px dashed rgb(255 255 255 / 0.42);
  border-radius: 14px;
  background: linear-gradient(135deg, var(--color-cta), var(--color-cta-dark));
  box-shadow: 0 11px 25px rgb(83 96 76 / 0.17);
  text-align: left;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.primary-action.is-dragging {
  border-color: white;
  border-style: solid;
  box-shadow:
    0 0 0 3px rgb(255 255 255 / 0.35),
    0 11px 25px rgb(83 96 76 / 0.25);
  transform: scale(1.01);
}

/* Les enfants avaleraient `dragleave` sans cette neutralisation. */
.primary-action > * {
  pointer-events: none;
}

/* Mêmes fleurs que sur les tuiles, mais dans des teintes claires : sur l'aplat
   olive, le rose poudré et le bleu ciel s'éteignaient. */
.primary-action::before,
.primary-action::after {
  position: absolute;
  z-index: 1;
  background-repeat: no-repeat;
  background-size: contain;
  content: "";
  pointer-events: none;
}

.primary-action::before {
  top: -8px;
  right: 34px;
  width: 17px;
  height: 17px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cg fill='%23fdf3ea'%3E%3Ccircle cx='8' cy='3.4' r='3'/%3E%3Ccircle cx='12.4' cy='6.6' r='3'/%3E%3Ccircle cx='10.7' cy='11.8' r='3'/%3E%3Ccircle cx='5.3' cy='11.8' r='3'/%3E%3Ccircle cx='3.6' cy='6.6' r='3'/%3E%3C/g%3E%3Ccircle cx='8' cy='8' r='2.2' fill='%23e0b26a'/%3E%3C/svg%3E");
}

.primary-action::after {
  bottom: -7px;
  left: 74px;
  width: 14px;
  height: 14px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cg fill='%23f3c9bd'%3E%3Ccircle cx='8' cy='3.4' r='3'/%3E%3Ccircle cx='12.4' cy='6.6' r='3'/%3E%3Ccircle cx='10.7' cy='11.8' r='3'/%3E%3Ccircle cx='5.3' cy='11.8' r='3'/%3E%3Ccircle cx='3.6' cy='6.6' r='3'/%3E%3C/g%3E%3Ccircle cx='8' cy='8' r='2.2' fill='%23fdf3ea'/%3E%3C/svg%3E");
}

.primary-action strong {
  display: block;
  color: white;
  font-family: var(--font-script);
  font-size: 25px;
  font-weight: 400;
  line-height: 1.05;
}

/*
 * Le vocabulaire de la galerie remonte jusqu'ici : cadre ivoire, ombre portée,
 * légère rotation, coin qui se décolle. Un polaroïd entier par action était
 * impossible — il en faut 150px de haut pour rester lisible, soit 450px pour
 * trois, très au-dessus de ce que laisse un iPhone SE. Il devient donc le
 * langage des icônes, pas celui des cartes.
 */
.mini-polaroid {
  position: relative;
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 49px;
  height: 49px;
  padding: 5px;
  border-radius: 2px;
  background: var(--color-ivory);
  box-shadow:
    0 8px 16px rgb(49 49 42 / 0.14),
    0 2px 4px rgb(49 49 42 / 0.08);
  transform: rotate(-4deg);
}

/* Un bout de ruban adhésif plutôt qu'un coin décollé : le carré isolé se
   lisait comme un défaut de rendu, là où le ruban dit « collé dans un
   album ». Translucide, il fonctionne aussi bien sur l'aplat olive que sur
   le papier. */
.mini-polaroid::after {
  position: absolute;
  top: -7px;
  left: 50%;
  width: 26px;
  height: 11px;
  border: 1px solid rgb(255 255 255 / 0.5);
  background: rgb(255 255 255 / 0.42);
  box-shadow: 0 1px 2px rgb(49 49 42 / 0.12);
  content: "";
  transform: translateX(-50%) rotate(-7deg);
}

.mini-polaroid svg {
  width: 20px;
  color: var(--color-olive);
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.6;
}

.primary-action__icon svg {
  color: var(--color-cta-dark);
}

/* --- Actions secondaires : deux tuiles côte à côte, inclinées en miroir comme
       les polaroïds de la galerie. Les mettre sur une rangée libère la hauteur
       qu'aurait coûté une troisième barre empilée. --- */
.secondary-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/*
 * Petites fleurs posées sur le trait de la bordure. Elles sont dessinées en SVG
 * intégré au CSS plutôt que découpées dans la planche d'aquarelle : à 13px, un
 * fragment de photo devient une tache illisible, un tracé reste net.
 *
 * `background-origin: border-box` est la clé : sans lui, les calques se
 * peignent à l'intérieur du cadre et les fleurs flottent au lieu de chevaucher
 * le trait.
 */
.polaroid-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 14px 8px 12px;
  color: var(--color-ink);
  border: 1px solid var(--color-line);
  border-radius: 14px;
  background: var(--color-paper);
  text-align: center;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

/*
 * Les fleurs doivent chevaucher le trait, donc déborder du cadre : un calque de
 * fond en serait tronqué, seuls des éléments décorateurs peuvent sortir. D'où
 * ces deux pseudo-éléments, posés à cheval sur la bordure.
 */
.polaroid-tile::before,
.polaroid-tile::after {
  position: absolute;
  z-index: 1;
  background-repeat: no-repeat;
  background-size: contain;
  content: "";
  pointer-events: none;
}

.polaroid-tile::before {
  top: -8px;
  left: 16px;
  width: 17px;
  height: 17px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cg fill='%23e8a59e'%3E%3Ccircle cx='8' cy='3.4' r='3'/%3E%3Ccircle cx='12.4' cy='6.6' r='3'/%3E%3Ccircle cx='10.7' cy='11.8' r='3'/%3E%3Ccircle cx='5.3' cy='11.8' r='3'/%3E%3Ccircle cx='3.6' cy='6.6' r='3'/%3E%3C/g%3E%3Ccircle cx='8' cy='8' r='2.2' fill='%23d7b144'/%3E%3C/svg%3E");
}

.polaroid-tile::after {
  right: 20px;
  bottom: -7px;
  width: 14px;
  height: 14px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cg fill='%23bcd3de'%3E%3Ccircle cx='8' cy='3.4' r='3'/%3E%3Ccircle cx='12.4' cy='6.6' r='3'/%3E%3Ccircle cx='10.7' cy='11.8' r='3'/%3E%3Ccircle cx='5.3' cy='11.8' r='3'/%3E%3Ccircle cx='3.6' cy='6.6' r='3'/%3E%3C/g%3E%3Ccircle cx='8' cy='8' r='2.2' fill='%23e0c079'/%3E%3C/svg%3E");
}

/* Semées différemment sur la seconde tuile : au même endroit, elles se
   liraient comme un motif imprimé plutôt que comme un décor posé à la main. */
.polaroid-tile:last-child::before {
  top: auto;
  right: 18px;
  bottom: -8px;
  left: auto;
}

.polaroid-tile:last-child::after {
  top: -7px;
  right: auto;
  bottom: auto;
  left: 22px;
}

.polaroid-tile:nth-child(2) .mini-polaroid {
  transform: rotate(3deg);
}

.polaroid-tile:active {
  transform: scale(0.97);
}

/* La légende manuscrite reste réservée à ces deux tuiles : à 49px et sur
   l'aplat olive de l'action principale, la Parisienne devient illisible. */
.polaroid-tile__caption {
  color: var(--color-olive);
  font-family: var(--font-script);
  font-size: 19px;
  line-height: 1;
}

@media (max-width: 759px) {
  .quick-share {
    padding: 22px 16px 18px;
  }

  .quick-share__copy > p {
    font-size: 13px;
  }

  .quick-actions {
    gap: 8px;
    margin-top: 16px;
  }

  .primary-action {
    min-height: 60px;
  }

  .polaroid-tile {
    padding: 12px 6px 10px;
  }
}

@media (min-width: 760px) {
  .quick-share {
    display: grid;
    grid-template-columns: 0.62fr 1.38fr;
    align-items: center;
    gap: 28px;
    max-width: 1060px;
    margin: -48px auto 0;
    padding: 24px;
    border-radius: 20px;
  }

  .quick-share__copy {
    padding-left: 13px;
    text-align: left;
  }

  .quick-share h2 {
    font-size: 31px;
  }

  /* Trois colonnes égales : à la taille de texte actuelle, un bouton
     principal plus large forçait les deux autres à replier leur libellé sur
     trois lignes. */
  .quick-actions {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin: 0;
  }

  /* `display: contents` aplatit le conteneur des deux tuiles : les trois
     actions redeviennent trois pistes égales de la même grille, comme avant. */
  .secondary-actions {
    display: contents;
  }

  .polaroid-tile {
    flex-direction: row;
    justify-content: flex-start;
    gap: 12px;
    min-height: 66px;
    padding: 8px 14px;
    text-align: left;
  }

  .primary-action strong {
    font-size: 23px;
  }
}
</style>
